import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../../context/AuthContext";
import { getWorkoutById } from "../../api/workouts";
import useScreenSize from "../../context/ScreenContext";
import ExerciseWeight from "../Display/ExerciseWeight";
import {
  CenteredRow,
  MainText,
  AllCenteredColumn,
  scrollBar,
} from "../../data/style/Style";

const MILE_CONVERT = 0.000621371192;
const GENDERS = {
  male: 0,
  female: 1,
};
const ChallengeWorkout = (props) => {
  let { wodId } = props;
  const { size, mobileDevice } = useScreenSize();
  var [width] = size;
  const { profile, settings } = useAuth();

  const workoutQuery = useQuery({
    queryKey: ["workout", parseInt(wodId)],
    queryFn: ({ signal }) => getWorkoutById({ signal, wodId }),
  });

  const timecap = workoutQuery.isFetched
    ? profile.gender === GENDERS.female
      ? workoutQuery.data?.female_timecap_min
      : workoutQuery.data.timecap_min
    : 0;

  return (
    <Box sx={{ background: "#687e69" }} p={1} borderRadius={"4px"}>
      {workoutQuery.isFetched && (
        <AllCenteredColumn height={"100%"} gap={1}>
          <Stack width={"100%"}>
            <Stack
              direction={"row"}
              gap={1}
              justifyContent={"center"}
              width={"100%"}
            >
              <MainText
                sx={{
                  textShadow: "0 4px 8px rgba(0, 0, 0, 0.5)",
                }}
              >
                {workoutQuery.data.type_name}
              </MainText>
              {workoutQuery.data.type !== 4 && (
                <MainText>({timecap} minutes)</MainText>
              )}
            </Stack>
          </Stack>
          <AllCenteredColumn height={"98%"}>
            {workoutQuery.data.structure_display &&
              workoutQuery.data.type === 1 && (
                <Box textAlign={"center"} mb={2}>
                  <MainText
                    sx={{
                      textShadow: "0 4px 8px rgba(0, 0, 0, 0.5)",
                    }}
                  >
                    {workoutQuery.data.structure_name}
                  </MainText>
                </Box>
              )}
            {workoutQuery.data.type === 1 && workoutQuery.data.rounds > 1 && (
              <Box textAlign={"center"} mb={2}>
                <MainText>{workoutQuery.data.rounds} rounds of:</MainText>
              </Box>
            )}
            {workoutQuery.data.type === 4 && (
              <Box textAlign={"center"} mb={2}>
                <MainText>
                  {workoutQuery.data.timecap === 0
                    ? `${workoutQuery.data.rounds} rounds: ${
                        workoutQuery.data.every
                      } minutes on / ${workoutQuery.data.rest} ${
                        workoutQuery.data.rest < 2 ? "minute" : "minutes"
                      } off`
                    : `Every ${workoutQuery.data.every} minutes for ${workoutQuery.data.timecap_min} minutes`}
                </MainText>
              </Box>
            )}
            <Stack
              gap={0.5}
              height={"15vh"}
              overflow={"auto"}
              sx={scrollBar}
              pr={0.2}
            >
              {workoutQuery.data.exercises.map((item, index) => {
                const reps =
                  item.reps === 5000
                    ? "max"
                    : profile.gender === GENDERS.male
                      ? item.reps
                      : item.female_reps;

                const unit =
                  item.units === "rep"
                    ? null
                    : item.units === "m" &&
                        settings.distance === 1 &&
                        item.category === 3
                      ? "mi"
                      : item.units;
                return (
                  <CenteredRow key={index} gap={width < 700 ? 0.5 : 0.5}>
                    {workoutQuery.data.type === 3 && (
                      <Box width={!mobileDevice ? "60px" : "38px"}>
                        <MainText>Min {index + 1}:</MainText>
                      </Box>
                    )}

                    {!workoutQuery.data.structure_display && (
                      <Stack direction={"row"} alignItems={"center"} gap={1}>
                        <MainText
                          sx={{
                            textShadow: "0 4px 8px rgba(0, 0, 0, 0.5)",
                          }}
                        >
                          {reps !== 0 && (
                            <>
                              {item.units === "m" &&
                              settings.distance === 1 &&
                              item.category === 3
                                ? (reps * MILE_CONVERT).toFixed(2)
                                : reps}{" "}
                            </>
                          )}
                        </MainText>
                        <MainText
                          sx={{
                            textShadow: "0 4px 8px rgba(0, 0, 0, 0.5)",
                          }}
                        >
                          {unit}
                        </MainText>
                      </Stack>
                    )}
                    <Stack direction={"row"} alignItems={"center"} gap={1}>
                      <MainText>{item.exercise_name}</MainText>
                      {item.exercise_name === "run" &&
                        item.reps !== 5000 &&
                        item.alt_reps > 0 && (
                          <MainText>
                            (
                            {settings.distance === 0
                              ? item.alt_reps + " m"
                              : (item.alt_reps * MILE_CONVERT).toFixed(2) +
                                " mi"}
                            )
                          </MainText>
                        )}
                      {(workoutQuery.data.type === 2 ||
                        workoutQuery.data.type === 5) &&
                        item.category === 3 &&
                        item.units === "m" && (
                          <MainText>
                            (1 rep ={" "}
                            {settings.distance === 0 ? "25 m" : "82 ft"})
                          </MainText>
                        )}
                      {item.category >= 4 && (
                        <ExerciseWeight
                          weight_info={item.weight_info}
                          small={true}
                        />
                      )}
                    </Stack>
                  </CenteredRow>
                );
              })}
              {workoutQuery.data.type === 5 && (
                <>
                  {workoutQuery.data.exercises.map((item, index) => {
                    const reps =
                      profile.gender === GENDERS.male
                        ? item.reps + item.step
                        : item.female_reps + item.female_step;

                    const unit =
                      item.units === "rep"
                        ? null
                        : item.units === "m" &&
                            settings.distance === 1 &&
                            item.category === 3
                          ? "mi"
                          : item.units;
                    return (
                      <CenteredRow key={index} gap={width < 700 ? 0.5 : 1}>
                        <MainText
                          sx={{
                            textShadow: "0 4px 8px rgba(0, 0, 0, 0.5)",
                          }}
                        >
                          {item.reps !== 0 &&
                            !workoutQuery.data.structure_display && (
                              <>
                                {item.units === "m" &&
                                settings.distance === 1 &&
                                item.category === 3
                                  ? reps * MILE_CONVERT.toFixed(2)
                                  : reps}{" "}
                              </>
                            )}
                          {unit}
                        </MainText>
                        <MainText>{item.exercise_name}</MainText>
                        {item.exercise_name === "run" && item.alt_reps > 0 && (
                          <MainText>
                            (
                            {settings.distance === 0
                              ? item.alt_reps * 2 + " m"
                              : (item.alt_reps * 2 * MILE_CONVERT).toFixed(2) +
                                " mi"}
                            )
                          </MainText>
                        )}
                        {item.category >= 4 && (
                          <ExerciseWeight
                            weight_info={item.weight_info}
                            small={true}
                          />
                        )}
                      </CenteredRow>
                    );
                  })}
                </>
              )}
            </Stack>
          </AllCenteredColumn>
        </AllCenteredColumn>
      )}
    </Box>
  );
};
export default ChallengeWorkout;
